import React, { createContext, useContext, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'

type EdgeType =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'rotate'
  | 'default'
  | null

interface CursorStyleContextType {
  startDragging: (edge?: EdgeType) => void
  stopDragging: () => void
}

const CursorStyleContext = createContext<CursorStyleContextType>({
  startDragging: () => {},
  stopDragging: () => {},
})

interface CursorStyleProviderProps {
  children: React.ReactNode
}

export const CursorStyleProvider: React.FC<CursorStyleProviderProps> = ({ children }) => {
  const [dragState, setDragState] = useState<{
    isDragging: boolean
    edge: EdgeType
  }>({
    isDragging: false,
    edge: 'default',
  })

  const startDragging = useCallback((edge: EdgeType = 'default') => {
    setDragState({ isDragging: true, edge })
  }, [])

  const stopDragging = useCallback(() => {
    setDragState({ isDragging: false, edge: 'default' })
  }, [])

  const getCursorStyle = (edge: EdgeType) => {
    if (edge === null) return 'default'

    const cursorMap = {
      top: 'ns-resize',
      bottom: 'ns-resize',
      left: 'ew-resize',
      right: 'ew-resize',
      'top-left': 'nwse-resize',
      'bottom-right': 'nwse-resize',
      'top-right': 'nesw-resize',
      'bottom-left': 'nesw-resize',
      rotate: 'move',
      default: 'default',
    } as const

    return cursorMap[edge as keyof typeof cursorMap]
  }

  return (
    <CursorStyleContext.Provider value={{ startDragging, stopDragging }}>
      {children}
      {dragState.isDragging &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              cursor: getCursorStyle(dragState.edge),
              zIndex: 9999,
              userSelect: 'none',
            }}
          />,
          document.body,
        )}
    </CursorStyleContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useCursorStyle = () => {
  const context = useContext(CursorStyleContext)
  if (!context) throw new Error('useCursorStyle must be used within CursorStyleProvider')
  return context
}
